// @flow

import * as React from 'react';

import uniqBy from 'lodash/uniqBy';

import { stopSubmit } from 'redux-form';

import type { DecoratedProduct } from '@gandi/cart';
import { CONTACTS_FORM } from '@gandi/domain-configuration';

import type { Role } from '@gandi/domain-configuration';
import type { NotifyAction } from '@gandi/notifications';
import type { ActionReturn } from '@gandi/resource/redux';
import type { TranslatorProps } from '@gandi/translate';
import { Badge, Base, Button, Cell } from '@gandi/ui';
import {
  Action,
  List,
  ListRow,
  ListItem,
  ListCell,
  ListCellGroup,
  ListIcon,
  ListLoader,
} from '@gandi/ui/deprecated';

import type { FormComponentProps } from 'gandi.react-form';
import type { GandiLogger } from 'gandi.redux-logger';

import { Layout } from '../../../../components/Layout';
import ListSpinner from '../../../../components/ListSpinner';
import Collapsible from '../../../../components/deprecated/Collapsible';

import type { WithRouterProps } from '../../../../types';

import { saveStepValues } from '../modules/actions';
import type {
  StartStepsValidationAction,
  RemoveDomainAction,
} from '../modules/actions';
import {
  computeFlag,
  getStepInfo,
  translationsHelper,
} from '../modules/helpers';
import { computePurchaseGroupId, getTld } from '../modules/selectors';
import type {
  GlobalErrors,
  InnerStep,
  InnerSteps,
  InnerStepFlag,
  Step,
  TrusteePrices,
  UIStep,
  UIStepRole,
  UISteps,
} from '../types';

import DrawerForm from './DrawerForm';
import SelectionScreen from './SelectionScreen';

export type InjectedProps = {
  form: FormComponentProps,
  translator: TranslatorProps,
  logger: GandiLogger,
};

export type OwnProps = {
  ...WithRouterProps<{
    cartId: string,
  }>,
};

export type StateProps = {
  backUrl: string,
  cartId: string,
  domainProducts: Array<DecoratedProduct>,
  paasCreateRenew: Array<DecoratedProduct>,
  globalErrors: GlobalErrors,
  innerSteps: InnerSteps,
  isCartWorking: boolean,
  isWizardReady: boolean,
  isDeleting: boolean,
  isWorking: boolean,
  isValidating: boolean,
  currentStep: ?Step,
  currentStepErrors: { [key: string]: string } | {},
  steps: Array<Step>,
  trusteePrices: ?TrusteePrices,
  uiSteps: UISteps,
  allStepsAreSuccessful: boolean,
};

export type DispatchProps = {
  initializeWizard: (translator: TranslatorProps) => Promise<void>,
  notifyDanger: NotifyAction,
  removeDomain: RemoveDomainAction<ActionReturn>,
  resetState: any,
  saveStepValues: typeof saveStepValues,
  setTrusteeRoleFlag: Role => Promise<void>,
  startStepsValidation: StartStepsValidationAction<ActionReturn>,
  stopSubmit: typeof stopSubmit,
  setInnerStepFlag: (
    innerStep: InnerStep,
    flag: InnerStepFlag,
    roleHasTrustee: boolean,
  ) => Promise<void>,
};

export type ExtraFormDispatchProps = {
  stopSubmit: (...args: any) => any,
};

export type Props = {
  ...InjectedProps,
  ...OwnProps,
  ...StateProps,
  ...DispatchProps,
};

export default class WizardPresenter extends React.Component<Props> {
  didInit: boolean = false;

  componentWillUnmount() {
    // Reset wizard state on unmount.
    // Note: this does not prevent asynchronous actions from a previous wizard
    // instance to update the state after unmounting, which may lead to
    // inconsistent states if a contact validation API call resolves after unmounting.
    this.props.resetState();
  }

  componentDidMount() {
    // Prevent multiple parallel initializations due to React strict mode's unmounting/remounting
    // in dev mode. This prevents getting inconsistent states in dev,
    // but could hide real issues (see `componentWillUnmount`'s comment).
    if (this.didInit) return;
    this.didInit = true;

    this.props.initializeWizard(this.props.translator);
  }

  translateKeys: any = translationsHelper(
    this.props.translator,
    this.props.logger,
  );

  // TODO This should probably be moved to its own component
  renderTitle: (
    uiStep: UIStep,
    domainCount: number,
    status: string,
  ) => React.Node = (uiStep: UIStep, domainCount: number, status: string) => {
    const stepInfo = getStepInfo(uiStep);
    return (
      <ListRow separatorBottom>
        <ListItem>
          <ListCellGroup>
            <Cell paddingLeft={null}>
              <Badge intent="info" marginRight="xsmall" transform="uppercase">
                {`.${stepInfo.tld}`}
              </Badge>{' '}
              <Badge variant="light" intent="warning">
                {this.translateKeys(stepInfo.process)}
              </Badge>
            </Cell>
            <ListCell>
              {status === 'progress' &&
                this.props.translator.__(
                  'Checking information for <for-translators-use>Checking information for **Owner contact** where Owner contact will change according to the role being displayed </for-translators-use> %(role)s',
                  { role: this.translateKeys(`contact-${stepInfo.role}`) },
                )}
              {status === 'error' &&
                this.props.translator.__(
                  'Complete information for <for-translators-use>Complete information for **Owner contact** where **Owner contact** will change according to the role being displayed </for-translators-use> %(role)s',
                  { role: this.translateKeys(`contact-${stepInfo.role}`) },
                )}
            </ListCell>
            <ListCell type="secondary">
              {this.props.translator.__('%(count)s domains', {
                count: domainCount,
              })}
            </ListCell>
          </ListCellGroup>
        </ListItem>
      </ListRow>
    );
  };

  renderGlobalErrors: (uiStep: UIStep) => null | React.Node = (
    uiStep: UIStep,
  ) => {
    const stepInfo = getStepInfo(uiStep);
    if (
      stepInfo.tld &&
      Array.isArray(
        this.props.globalErrors[stepInfo.purchaseGroupId]?.[stepInfo.tld]
          ?.global_errors,
      )
    ) {
      const tldErrors = this.props.globalErrors[stepInfo.purchaseGroupId][
        stepInfo.tld
      ].global_errors.map((error, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <p key={index}>{error}</p>
      ));
      return (
        <Base padding="small" backgroundColor="surface">
          {tldErrors}
        </Base>
      );
    }
    return null;
  };

  handleClickCancel: (uiStep: UIStep) => () => void =
    (uiStep: UIStep) => () => {
      const stepInfo = getStepInfo(uiStep);
      const roleHasTrustee = !!(
        stepInfo.tld &&
        this.props.globalErrors[stepInfo.purchaseGroupId]?.[stepInfo.tld]
          ?.trustee_roles.length > 0
      );

      // Transition 'form' -> 'menu'
      this.props.setInnerStepFlag(
        {
          purchaseGroupId: stepInfo.purchaseGroupId,
          tld: stepInfo.tld,
          // $FlowFixMe[incompatible-call]
          process: stepInfo.process,
          role: uiStep.roles[uiStep.roles.length - 1].label,
        },
        'menu',
        roleHasTrustee,
      );
    };

  /**
   * Remove products from cart and from wizard steps
   */
  removeProductHandler: (SyntheticEvent<>) => Promise<mixed | null> =
    async evt => {
      const { translator } = this.props;

      evt.preventDefault();

      // remove the product from the cart and from the wizard
      if (this.props.currentStep) {
        try {
          await this.props.removeDomain(
            this.props.currentStep.purchaseGroupId,
            this.props.currentStep.domainInfo.tld,
            // $FlowFixMe[incompatible-call]
            this.props.currentStep.domainInfo.process,
          );
        } catch (e) {
          return this.props.notifyDanger(
            translator.__('Unexpected error occurred'),
          );
        }

        try {
          await this.props.startStepsValidation();
        } catch (e) {
          return this.props.notifyDanger(
            translator.__('Unexpected error occurred'),
          );
        }
        if (this.props.allStepsAreSuccessful) {
          return this.props.navigate(this.props.backUrl);
        }
      }
      return null;
    };

  /**
   * Renders the selection screen to pick between
   * Edit, Trustee, Account Choice and Remove options
   */
  renderSelectionScreen: (uiStep: UIStep, domainCount: number) => React.Node = (
    uiStep: UIStep,
    domainCount: number,
  ) => {
    const stepInfo = getStepInfo(uiStep);
    return (
      <>
        {stepInfo.status &&
          this.renderTitle(uiStep, domainCount, stepInfo.status)}
        {this.renderGlobalErrors(uiStep)}
        {/* $FlowFixMe[incompatible-type] handleSubmitTrustee should not be undefined */}
        <SelectionScreen
          setTrusteeRoleFlag={this.props.setTrusteeRoleFlag}
          backUrl={this.props.backUrl}
          cartId={this.props.cartId}
          domainCount={domainCount}
          globalErrors={this.props.globalErrors}
          handleSubmitTrustee={this.props.form.handleSubmit}
          logger={this.props.logger}
          removeProductHandler={this.removeProductHandler}
          isDeleting={this.props.isDeleting}
          setInnerStepFlag={this.props.setInnerStepFlag}
          translator={this.props.translator}
          uiStep={uiStep}
          trusteePrices={this.props.trusteePrices}
          role={stepInfo.role}
          tld={stepInfo.tld}
        />
      </>
    );
  };

  /**
   * Renders a form for a given role of a tld for a process
   */
  renderStepRole: (
    uiStep: UIStep,
    role: UIStepRole,
  ) => null | Array<?(boolean | React.Node)> = (
    uiStep: UIStep,
    role: UIStepRole,
  ) => {
    // $FlowFixMe[incompatible-type]
    const flag = `${uiStep.purchaseGroupId}_${uiStep.domainInfo.tld}_${uiStep.domainInfo.process}_${role.label}`;
    const innerStepFlag = this.props.innerSteps[flag];

    if (!(role.status === 'success' || innerStepFlag === 'form')) {
      return null;
    }

    const contactTypesTranslator = translationsHelper(
      this.props.translator,
      this.props.logger,
    );

    const [icon, itemHighlight, fill] =
      role.status === 'success'
        ? ['checkmark', 'success', 'success']
        : ['warning', 'danger', 'warning'];

    const step: Step = {
      purchaseGroupId: uiStep.purchaseGroupId,
      domainInfo: uiStep.domainInfo,
      role: role.label,
      // $FlowFixMe[incompatible-type]
      values: role.values,
    };

    const formButtons = [
      <Button
        onClick={this.handleClickCancel(uiStep)}
        key="cancel"
        type="button"
        disabled={this.props.isWorking}
        kind="outline"
        grouped
      >
        {this.props.translator.__('Cancel')}
      </Button>,
      <Button
        form={CONTACTS_FORM}
        loading={this.props.isWorking}
        key="submit"
        grouped
      >
        {this.props.translator.__('Submit')}
      </Button>,
    ];

    return [
      <ListRow key="form-header" separatorBottom>
        <ListItem highlight={itemHighlight}>
          <ListCell>{contactTypesTranslator(`contact-${role.label}`)}</ListCell>
          {role.status === 'progress' ? (
            <ListLoader />
          ) : (
            <ListIcon name={icon} fill={fill} />
          )}
        </ListItem>
      </ListRow>,
      innerStepFlag === 'form' &&
        this.props.currentStep &&
        this.props.currentStep.domainInfo.tld === step.domainInfo.tld &&
        this.props.currentStep.role === step.role && (
          <DrawerForm
            key={flag}
            step={this.props.currentStep}
            isWorking={this.props.isWorking}
            form={this.props.form}
            formButtons={formButtons}
            stopSubmit={this.props.stopSubmit}
            currentStepErrors={this.props.currentStepErrors}
          />
        ),
    ];
  };

  /**
   * Renders a step (tld x process) of the wizard
   */
  renderStep: (uiStep: UIStep) => React.Node = (uiStep: UIStep) => {
    const {
      purchaseGroupId,
      tld,
      process,
      status,
      role: uiRole,
    } = getStepInfo(uiStep);
    const flag = computeFlag(uiStep);
    const innerStepFlag: InnerStepFlag = this.props.innerSteps[flag];
    const domainCount = uniqBy(
      this.props.domainProducts
        .filter(
          domain =>
            computePurchaseGroupId(domain) === purchaseGroupId &&
            getTld(domain) === tld &&
            domain.process === process,
        )
        .concat(
          this.props.paasCreateRenew.filter(
            paas =>
              process === 'create' && // check that the current count is on domain create
              paas.payload.domain?.tld &&
              paas.payload.domain.tld === tld,
          ),
        ),
      product => product.payload.fqdn || product.payload.domain?.fqdn,
    ).length;

    if (innerStepFlag === 'menu' && status === 'error') {
      return this.renderSelectionScreen(uiStep, domainCount);
    }
    return (
      <Collapsible
        id={`c-domain-${uiStep.domainInfo.fqdn || tld}`}
        key={uiStep.domainInfo.fqdn || tld}
        collapsed={uiStep.collapsed}
        render={(getActionProps, getDrawerProps) => (
          <>
            <ListRow separatorBottom>
              <ListItem>
                <ListCellGroup>
                  <Cell paddingLeft={null}>
                    <Badge
                      intent="info"
                      marginRight="xsmall"
                      transform="uppercase"
                    >
                      {`.${tld}`}
                    </Badge>{' '}
                    <Badge variant="light" intent="warning">
                      {translationsHelper(
                        this.props.translator,
                        this.props.logger,
                        // $FlowFixMe[incompatible-call]
                      )(process)}
                    </Badge>
                  </Cell>
                  <ListCell>
                    {status === 'progress'
                      ? this.props.translator.__(
                          'Checking information for <for-translators-use>Checking information for **Owner contact** where Owner contact will change according to the role being displayed </for-translators-use> %(role)s',
                          {
                            role: this.translateKeys(`contact-${uiRole}`),
                          },
                        )
                      : this.props.translator.__('Contacts Information')}
                    {uiStep.collapsed && (
                      <ListIcon name="checkmark" fill="success" />
                    )}
                  </ListCell>
                  <ListCell type="secondary">
                    {this.props.translator.__('%(count)s domains', {
                      count: domainCount,
                    })}
                  </ListCell>
                </ListCellGroup>
              </ListItem>
              <Action {...getActionProps()} />
            </ListRow>
            <Base {...getDrawerProps()}>
              {uiStep.roles.map(role => this.renderStepRole(uiStep, role))}
            </Base>
          </>
        )}
      />
    );
  };

  /**
   * Render the wizard step and form.
   */
  renderWizard(): React.Node {
    return (
      <List
        items={this.props.uiSteps}
        itemId={(contactInformation, idx) => `domain-${idx}`}
      >
        {this.renderStep}
      </List>
    );
  }

  renderSpinner(message: string): React.Node {
    return <ListSpinner message={message} />;
  }

  decorateContent(...content: Array<React.Node>): React.Node {
    return <Layout currentStep={2}>{React.Children.toArray(content)}</Layout>;
  }

  render(): React.Node {
    const {
      allStepsAreSuccessful,
      isWizardReady: ready,
      isCartWorking: loading,
      isDeleting: deleting,
      translator: { __ },
    } = this.props;

    if (allStepsAreSuccessful) {
      return this.decorateContent(
        this.renderSpinner(
          ready ? __('Updating your products') : __('Preparing your products'),
        ),
      );
    }

    return this.decorateContent(
      this.renderWizard(),
      deleting
        ? this.renderSpinner(__('Deleting products from your cart'))
        : loading && this.renderSpinner(__('Checking contact information')),
    );
  }
}
